"use client";

import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { parseLiveRatesPayload, type LiveRates } from "@/lib/liveRates";

const POLL_INTERVAL_MS = 30_000;

const LiveRatesContext = createContext<LiveRates | undefined>(undefined);

// Single, page-wide poller for the real gold-price API. Every component that
// shows a rate (header ticker, Live Prices section, calculator, purity
// table...) reads from this one context value instead of fetching or
// animating its own copy, so they can never show different numbers for the
// same instant — there is exactly one number, updated in one place.
export function LiveRatesProvider({
  initialLiveRates,
  children,
}: {
  initialLiveRates?: LiveRates;
  children: ReactNode;
}) {
  const [rates, setRates] = useState(initialLiveRates);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch("/api/gold-price", { cache: "no-store" });
        if (!res.ok) return;
        const parsed = parseLiveRatesPayload(await res.json());
        if (parsed && !cancelled) setRates(parsed);
      } catch {
        // Network hiccup — keep showing the last known-good value.
      }
    }

    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <LiveRatesContext.Provider value={rates}>
      {children}
    </LiveRatesContext.Provider>
  );
}

export function useLiveRatesContext(): LiveRates | undefined {
  return useContext(LiveRatesContext);
}
